import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Combobox, Expander, type ComboOption } from "../../design/components";
import { useEntities } from "../../api/queries";
import { useRoles } from "../../api/users";
import { serverText } from "../../i18n/localized";
import { UsersTable } from "./UsersTable";
import styles from "./users.module.css";

export const USERS_EXPLAINER =
  "Each company names its own administrator, who invites their colleagues and sets their roles; the central team issues only that first invitation.";

export function UsersPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const entities = useEntities();
  const roles = useRoles();

  const companies = useMemo(() => entities.data ?? [], [entities.data]);
  const requested = searchParams.get("entity");
  const selected = companies.find((company) => company.id === requested) ?? companies[0];

  // A bare /users is rewritten so the selected company is always linkable.
  useEffect(() => {
    if (!selected || requested === selected.id) return;
    const next = new URLSearchParams(searchParams);
    next.set("entity", selected.id);
    setSearchParams(next, { replace: true });
  }, [requested, searchParams, selected, setSearchParams]);

  const options = useMemo<ComboOption[]>(
    () => companies.map((company) => ({ v: company.id, n: company.legalName, d: company.code })),
    [companies],
  );

  function pick(entityId: string) {
    const next = new URLSearchParams(searchParams);
    next.set("entity", entityId);
    setSearchParams(next);
  }

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{t("users.title", "Users and access")}</h1>
          <p className={styles.lede} data-testid="users-explainer">
            {t("users.explainer", USERS_EXPLAINER)}
          </p>
        </div>
      </div>

      <div className={styles.selector}>
        <label className={styles.selectorLabel} htmlFor="users-company">
          {t("users.company", "Company")}
        </label>
        <Combobox
          id="users-company"
          value={selected?.legalName ?? ""}
          options={options}
          ariaLabel={t("users.company", "Company")}
          placeholder={t("users.searchCompanies", "Search companies…")}
          disabled={!companies.length}
          onPick={pick}
        />
      </div>

      <div className={`card ${styles.legend}`}>
        <Expander
          head={t("users.roleLegend", "What each role can do")}
          body={
            <div className={styles.roleGrid}>
              {(roles.data ?? []).map((role) => (
                <div key={role.key} className={styles.roleCard}>
                  <div className={styles.roleName}>{serverText(`domain.role.${role.key}.name`, role.name)}</div>
                  <div className={styles.roleDesc}>{serverText(`domain.role.${role.key}.description`, role.description)}</div>
                </div>
              ))}
            </div>
          }
        />
      </div>

      {selected ? <UsersTable entityId={selected.id} /> : null}
    </>
  );
}
